import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { startCase } from "lodash";
import moment from "moment";
import "moment/locale/es-mx";

import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import type { BasePost, Category, Post } from "../../src/types/blog";

import {
  getAllCategories,
  getAllPostsPaths,
  getPostBySlug,
  getRecentPosts,
} from "../../src/cms-blog-api/queries/query-functions";
import RightArrowIcon from "../../public/img/arrow-right-for-next-button.svg";
import { urlFor, usePreviewSubscription } from "../../src/cms-blog-api/sanity";
import PostBody from "../../components/blog/postBody";
import BlogLayout from "../../components/blog/blogLayout";
import SideBar from "../../components/blog/sideBar";
import PostLoader from "../../components/blog/loaders/postLoader";
import { postBySlugQuery } from "../../src/cms-blog-api/queries/query-strings";

type PostPageProps = {
  post: Post;
  categories: Category[];
  recentPosts: BasePost[];
};

type PostPageParams = {
  "post-slug": string;
};

const PostPage: NextPage<PostPageProps> = ({
  post,
  categories,
  recentPosts,
}) => {
  const { isFallback, isPreview } = useRouter();

  const { data: postData } = usePreviewSubscription(postBySlugQuery, {
    initialData: post,
    enabled: isPreview && !!post.slug,
    params: { postSlug: post?.slug },
  });

  if (isFallback) {
    return (
      <BlogLayout>
        <PostLoader />
      </BlogLayout>
    );
  }

  return (
    <BlogLayout
      pageTitle={postData.seoData?.title}
      pageImageUrl={
        postData.image.asset &&
        urlFor(postData.image).width(1200).height(630).url()
      }
      pageDescription={postData.seoData?.metaDescription}
      authorName={postData.author?.name}
      isArticle
    >
      <div className="grid grid-cols-3 my-10 gap-x-12 px-2">
        <div className="col-span-3 lg:col-span-2 space-y-10">
          <div className="rounded-lg shadow-post-image relative aspect-w-16 aspect-h-9">
            {postData.image.asset && (
              <Image
                className="rounded-lg"
                src={urlFor(postData.image).url()}
                layout="fill"
                objectFit="cover"
                alt={postData.image.altText}
                placeholder="blur"
                blurDataURL={postData.image.asset.metadata.lqip}
                sizes="50vw"
              />
            )}
          </div>

          <div className="space-y-2.5">
            <h1 className="font-bold">{postData.title}</h1>
            <span className="text-xs text-gray-500">
              <span>
                {startCase(moment(postData.createdAt).format("D MMMM YYYY"))}
              </span>
              <span className="font-bold">{` | `}</span>
              <span>{postData.category?.name}</span>
              <span className="font-bold">{` | `}</span>
              <span>{postData.author.name}</span>
            </span>
            <PostBody blocks={postData.content} />
          </div>

          <div>
            {postData.lillyCode && (
              <div className="relative h-20 w-60">
                <Image
                  src="/img/bi-lilly-all.png"
                  layout="fill"
                  objectFit="contain"
                  alt="BI | Lilly Logo"
                  sizes="50vw"
                />
              </div>
            )}

            <div className="text-sm text-gray-800">
              <p>{postData.biCode}</p>
              <p>{postData.lillyCode}</p>
            </div>
          </div>

          {!isPreview && (
            <div
              className={`flex flex-row flex-wrap gap-y-2 justify-center ${
                post.prevPost ? "sm:justify-between" : "sm:justify-end"
              }`}
            >
              {post.prevPost && (
                <Link
                  href={`/blog/${post.prevPost.slug}`}
                  className="text-sm font-medium flex flex-row items-center gap-x-1 text-bi-green-malachite-800 hover:underline w-max"
                >
                  <span>
                    <RightArrowIcon className="w-4 h-4 transform rotate-180" />
                  </span>
                  <span>Publicación anterior</span>
                </Link>
              )}

              {post.nextPost && (
                <Link
                  href={`/blog/${post.nextPost.slug}`}
                  className="text-sm font-medium flex flex-row items-center gap-x-1 text-bi-green-malachite-800 hover:underline w-max"
                >
                  <span>Publicación siguiente</span>
                  <span>
                    <RightArrowIcon className="w-4 h-4" />
                  </span>
                </Link>
              )}
            </div>
          )}
        </div>

        <div className="hidden lg:block bg-white rounded-lg shadow-lg px-8 py-10 space-y-10">
          <SideBar recentPosts={recentPosts} categories={categories} />
        </div>
      </div>
    </BlogLayout>
  );
};

export default PostPage;

export const getStaticPaths: GetStaticPaths<PostPageParams> = async () => {
  const postsPaths = await getAllPostsPaths();

  return {
    paths: postsPaths.map((slug) => ({ params: { "post-slug": slug } })),
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps<
  PostPageProps,
  PostPageParams
> = async ({ params, preview, previewData }) => {
  const post = await getPostBySlug(
    params["post-slug"],
    preview,
    previewData as string
  );

  if (post) {
    const categories = preview ? [] : await getAllCategories();
    const recentPosts = preview ? [] : await getRecentPosts();

    return {
      props: {
        post,
        categories,
        recentPosts,
      },
      revalidate: 5,
    };
  }

  return {
    notFound: true,
  };
};
